import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy,
  inject,
  signal,
  untracked,
} from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, max, min, required, validate, ValidationError } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { MatDatepicker } from '@angular/material/datepicker'
import { MatSnackBar } from '@angular/material/snack-bar'

import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { arvonMuutokset, asetaArvo, nollaa } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { VasenValikkoService } from '../../_jaettu-angular/service/vasen-valikko.service'

import { DateService } from '@lemon/core/service/date.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'
import { Asiakas, AsiakkaanKuukausihinta, AsiakkaanMuuJatkuvaVeloitus, TyokieltoHistoriaEntry } from '../../_jaettu-lemonator/model/asiakas'
import { AsiakasJaettuService } from '../../_jaettu-lemonator/service/asiakas-jaettu.service'
import { AsiakkaanSopimuskausiService } from '../../_jaettu-lemonator/service/sopimuskausi.service'
import { VeloituksetService } from '../../_jaettu-lemonator/service/veloitukset-service'

import { LaskutettavatPalvelut, LaskutusFrekvenssi, UUDEN_LASKUN_AVAIN } from '@lemon/core-firebase/model/lasku'
import { Tuotenimike } from '@lemon/core-firebase/model/tuotenimikkeet'

import { AsiakasKopioija } from '../../_angular/service/asiakas/asiakas.kopioija'
import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { KirjanpitajaService } from '../../_angular/service/kirjanpitaja/kirjanpitaja.service'

import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs'
import { map, startWith, switchMap, takeUntil } from 'rxjs/operators'

import { MatInput } from '@angular/material/input'
import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { ApixReceivedInvoiceConfig } from '@lemon/core-firebase/model/apix'
import { SahkoisenLaskunValittajaService, Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { OptionLabelsService } from '@lemon/core-firebase/service/option-labels.service'
import { TuotteetService } from '@lemon/core-firebase/service/tuotteet-service'
import { LocalDate } from '@lemon/core/model/common'
import { StringService } from '@lemon/core/service/string.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'

interface HintaRivi {
  voimassaAlkaen: Date | null
  hinta: number | null
  syy: string
}

interface MuuJatkuvaVeloitusRivi {
  tuote: LaskutettavatPalvelut | null
  hinta: number | null
  frekvenssi: LaskutusFrekvenssi | null
  voimassaAlkaen: Date | null
  paattyy: Date | null
  lisatiedot: string
}

interface LaskutusLomake {
  hinnanPerusteet: string
  hinnat: HintaRivi[]
  muutJatkuvatVeloitukset: MuuJatkuvaVeloitusRivi[]
  lisaavastaanottaja: string
  viitenumero: string
  spostiVaiEmail: Laskutustyyppi | null
  sahkoinenLaskuValittaja: string | Verkkolaskuoperaattori | null
  sahkoinenLaskuOsoite: string
  // laskunKieli: TuettuKieli
  estaAlvLaskut: boolean | null
}

interface NollahintaLomake {
  kuukausi: Date | null
  syy: string
}

type Rivitaulukko = 'hinnat' | 'muutJatkuvatVeloitukset'

/** Rivin luontihetkellä määräytyvät asetukset, joita reaktiivinen lomake piti kontrollien käytöstilana. */
interface RivinAsetukset {
  lukossa?: boolean
  paattyyFilter?: (date: Date | null) => boolean
}

const TYHJA_NOLLAHINTA: NollahintaLomake = { kuukausi: null, syy: '' }

function signaaliVirheet(virheet: ValidationErrors | null): ValidationError[] | null {
  return virheet ? Object.keys(virheet).map((laji): ValidationError => ({ kind: laji })) : null
}

function rivinIndeksi(polku: readonly string[]): number {
  return Number(polku[polku.length - 2])
}

interface MuuVeloitusEsikatselu {
  kuukausi: string
  rivit: { nimi: string; hinta: number }[]
  yhteensa: number
}

type Laskutustyyppi = 'spostilasku' | 'verkkolasku'
@Component({
  templateUrl: './laskutus.component.html',
  styleUrls: ['./laskutus.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakasAsetuksetLaskutusComponent implements OnInit, OnDestroy, AfterContentChecked {
  @ViewChildren('hintaInputti', { read: MatInput }) hintaKomponentit: QueryList<MatInput>

  private ngUnsubscribe: Subject<void> = new Subject<void>()

  naytaSuperObservable: Observable<boolean>
  onkoDevaajaTaiKayttotukiObservable: Observable<boolean>
  verkkolaskuoperaattorit: Observable<Verkkolaskuoperaattori[]>
  apixForwardConfigObservable: Observable<ApixReceivedInvoiceConfig>
  tyokieltoHistoriaObservable: Observable<TyokieltoHistoriaEntry[]>
  seuraavaLaskuObservable: Observable<MuuVeloitusEsikatselu>
  veloitustenTilaIlmoitusObservable: Observable<string | null>

  private readonly _injector = inject(Injector)
  private readonly _rivienAsetukset = new WeakMap<object, RivinAsetukset>()
  private readonly _rivienTilaukset = new Map<Rivitaulukko, Subscription[]>()
  private readonly _tekstikenttienKasittelijat = new Map<object, (arvo: string | null) => void>()
  private readonly _vastaanottajaPuuttuu = signal(false)
  private readonly _nollahinnanTarkistus = signal(0)

  lomake = form(
    signal<LaskutusLomake>({
      hinnanPerusteet: '',
      hinnat: [],
      muutJatkuvatVeloitukset: [],
      lisaavastaanottaja: '',
      viitenumero: '',
      spostiVaiEmail: null,
      sahkoinenLaskuValittaja: '',
      sahkoinenLaskuOsoite: '',
      // laskunKieli: null,
      estaAlvLaskut: null,
    }),
    polku => {
      validate(polku.hinnat, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
      applyEach(polku.hinnat, rivi => {
        disabled(rivi, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        required(rivi.voimassaAlkaen)
        // Edellinen rivi luetaan seuraamatta, koska reaktiivinen lomake tarkisti päivän vain sen oman arvon muuttuessa.
        validate(rivi.voimassaAlkaen, ({ value, pathKeys }) => {
          const arvo = value()
          return signaaliVirheet({ ...untracked(() => this.edellinenKuukausiValidator(arvo, rivinIndeksi(pathKeys()))), ...this.minimiKuukausValidator(arvo) })
        })
        required(rivi.hinta)
        min(rivi.hinta, 0.0)
        max(rivi.hinta, 2000)
        required(rivi.syy)
      })
      applyEach(polku.muutJatkuvatVeloitukset, rivi => {
        disabled(rivi.tuote, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        disabled(rivi.hinta, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        disabled(rivi.frekvenssi, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        disabled(rivi.voimassaAlkaen, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        required(rivi.tuote)
        required(rivi.hinta)
        max(rivi.hinta, 2000)
        validate(rivi.hinta, ({ value, valueOf }) => signaaliVirheet(this.hintaValidator(value(), valueOf(rivi.tuote))))
        required(rivi.frekvenssi)
        required(rivi.voimassaAlkaen)
        validate(rivi.voimassaAlkaen, ({ value }) => signaaliVirheet({ ...this.minimiKuukausValidator(value()), ...this.voimassaAlkaenDayValidator(value()) }))
        // Alkamispäivän muutos tarkisti päättymispäivän uudelleen päivämäärävalitsimen min-sidonnan kautta, frekvenssin muutos ei.
        validate(rivi.paattyy, ({ value, valueOf }) => {
          const paattyy = value()
          const alkaa = valueOf(rivi.voimassaAlkaen)
          return untracked(() => signaaliVirheet(this.paattymisPaivamaaraValidator(paattyy, alkaa, valueOf(rivi.frekvenssi))))
        })
      })
      // Puuttuvan vastaanottajan virhe korvasi reaktiivisessa lomakkeessa muut virheet, kunnes kenttä tarkistettiin uudelleen.
      validoiReaktiivisesti(polku.lisaavastaanottaja, kontrolli => this.validateEmail(kontrolli), { when: () => !this._vastaanottajaPuuttuu() })
      validate(polku.lisaavastaanottaja, () => (this._vastaanottajaPuuttuu() ? { kind: 'min' } : null))
      validoiReaktiivisesti(polku.viitenumero, kontrolli => this.validateViitenumero(kontrolli))
      required(polku.spostiVaiEmail)
      required(polku.sahkoinenLaskuValittaja, { when: ({ valueOf }) => valueOf(polku.spostiVaiEmail) === 'verkkolasku' })
      required(polku.sahkoinenLaskuOsoite, { when: ({ valueOf }) => valueOf(polku.spostiVaiEmail) === 'verkkolasku' })
      validoiReaktiivisesti(polku.sahkoinenLaskuOsoite, kontrolli => this._annaSahkoisenLaskunOsoitteenValidointiFunktio()(kontrolli), {
        when: ({ valueOf }) => valueOf(polku.spostiVaiEmail) === 'verkkolasku',
      })
      // Totuusarvon false pitää kelvata, joten käytetään reaktiivista Validators.required-validaattoria.
      validoiReaktiivisesti(polku.estaAlvLaskut, Validators.required)
    },
  )
  minMonth = new Date()
  asiakas: Asiakas = null
  namename = 'asdf ' + Math.random()
  readonly naytaSahkoinenLaskutus = signal(false)
  readonly asiakkaanOsoitetiedotPuuttuvat = signal(true)
  readonly isLastPriceZero = signal(false)
  readonly onSuper = signal(false)
  nollahintaLomake = form(signal<NollahintaLomake>(TYHJA_NOLLAHINTA), polku => {
    required(polku.kuukausi)
    validate(polku.kuukausi, ({ value }) => {
      this._nollahinnanTarkistus()
      return signaaliVirheet(this._nollahinnanKuukausiValidator(value()))
    })
    required(polku.syy)
  })
  nollahinnanMaksimiKuukausi: Date
  private _lisatytNollahinnat = new Set<object>()

  apixConfigUriObservable: Observable<string>
  apixConfigEncodedUriObservable: Observable<string>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>

  readonly muutJatkuvatVeloituksetTuotteet = signal<Tuotenimike[]>([])
  private _esikatseluKuukausia = new BehaviorSubject<number>(1)
  private _poistetutVeloitukset: AsiakkaanMuuJatkuvaVeloitus[] = []
  naytaPoistetut = false
  readonly onPoistettujaVeloituksia = signal(false)
  readonly kirjanpitajatMap = signal<Map<string, any>>(new Map())

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _route: ActivatedRoute,
    private _router: Router,
    private _asiakasService: AsiakasService,
    private _asiakasUriService: AsiakasUriService,
    private _dateService: DateService,
    private _stringService: StringService,
    private _asiakasKopioija: AsiakasKopioija,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _vasenValikkoService: VasenValikkoService,
    private _snackbar: MatSnackBar,
    private _viitenumeroService: ViitenumeroService,
    private _timestampService: TimestampService,
    private _firebase: FirebaseLemonator,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
    private _fileSaverService: FileSaverService,
    private _debugService: DebugService,
    private _veloituksetService: VeloituksetService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _sopimuskausiService: AsiakkaanSopimuskausiService,
    private _tuotteetService: TuotteetService,
    private _kirjanpitajaService: KirjanpitajaService,
    private _optionLabelsService: OptionLabelsService,
  ) {
    this.asiakas = this._asiakasKopioija.annaUusiAsiakas()
    this.naytaSuperObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      map(kirjanpitajanTiedot => {
        if (kirjanpitajanTiedot) {
          return kirjanpitajanTiedot.rooli === KirjanpitajanRooli.SUPER
        }
        return false
      }),
    )

    // Debug thingies
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.onkoDevaajaTaiKayttotukiObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable
    this.apixConfigUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return 'customers/' + avainTiedot.avain + '/apix-received-invoice-config/' + avainTiedot.avain
        }
        return ''
      }),
    )
    this.apixConfigEncodedUriObservable = this.apixConfigUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLinkLemonaid(uri)
      }),
    )

    this.veloitustenTilaIlmoitusObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas || !asiakas.muutJatkuvatVeloitukset || asiakas.muutJatkuvatVeloitukset.length === 0) {
          return null
        }
        const nyt = this._dateService.dateToLocalDate(new Date())
        const sopimusOnVoimassa = this._asiakasJaettuService.onkoSopimusVoimassaAinakinYhdenPaivanKuukaudessa(asiakas.sopimuskaudet, nyt)
        const kuukaudenAlku: LocalDate = { year: nyt.year, month: nyt.month, day: 1 }
        const kuukaudenLoppu = this._dateService.kuukaudenViimeinenPaikallinen(kuukaudenAlku)
        const tauko = this._sopimuskausiService.annaKokoAikavalinVoimassaOlevaTauko(asiakas, kuukaudenAlku, kuukaudenLoppu)

        if (!sopimusOnVoimassa) {
          return 'Asiakkaan sopimus ei ole voimassa – jatkuvia veloituksia ei sillä aikaa laskuteta.'
        }
        if (tauko && tauko.tyyppi === 'poytalaatikko') {
          return 'Asiakas on pöytälaatikossa – vain pöytälaatikossa sallitut veloitukset laskutetaan sillä aikaa.'
        }
        return null
      }),
    )
  }

  ngAfterContentChecked() {
    // this.fieldErrors = this.validationService.updateValidationStatus('asiakas', this.form)
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  private minimiKuukausValidator = (arvo: Date): ValidationErrors | null => {
    if (arvo) {
      const minDate = new Date()
      if (this._dateService.kuukausiaValissa(arvo, minDate) <= 0) {
        return { min: true }
      }
    }
    return null
  }

  private _nollahinnanKuukausiValidator = (arvo: Date): ValidationErrors | null => {
    if (!arvo) {
      return null
    }
    if (this._dateService.kuukausiaValissa(arvo, new Date()) > 0) {
      return { tulevaisuudessa: true }
    }
    const kuukausi = this._dateService.dateToLocalMonth(arvo)
    if (this.asiakas?.hinnat?.some(hinta => this._dateService.compareLocalMonths(hinta.voimassaAlkaen, '==', kuukausi))) {
      return { kuukaudellaOnJoHinta: true }
    }
    return null
  }

  private hintaValidator(hinta: number, tuote: LaskutettavatPalvelut): ValidationErrors | null {
    if (hinta == null) {
      return null
    }

    if (tuote === LaskutettavatPalvelut.KAMPANJAHYVITYS) {
      if (hinta >= 0) {
        return { kampanjahyvitysOltavaNegatiivinen: true }
      }
    } else if (hinta <= 0) {
      return hinta === 0 ? { notZero: true } : { eiVoiOllaNegatiivinen: true }
    }

    return null
  }

  validateEmail: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    return Validators.email(ctrl)
  }

  private validateViitenumero: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._viitenumeroService.onkoViitenumeroValidi(ctrl.value)) {
      return { viitenumero: true }
    }
    return null
  }

  async ngOnInit() {
    this._kirjanpitajaService.kirjanpitajienNimitiedotMapObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(kirjanpitajat => {
      this.kirjanpitajatMap.set(kirjanpitajat)
    })

    const d = new Date()
    d.setDate(1)
    this.minMonth = this._dateService.lisaaKuukausia(d, 1)
    this.nollahinnanMaksimiKuukausi = this._dateService.kuukaudenViimeinen(new Date())

    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    this.muutJatkuvatVeloituksetTuotteet.set(await this._tuotteetService.annaMuutJatkuvatVeloituksetTuotteet())

    muutokset(this.lomake.hinnat)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        const rivit = this.lomake.hinnat().value()
        this.isLastPriceZero.set(rivit?.length > 1 && rivit[rivit.length - 1]?.hinta === 0)
      })

    muutokset(this.lomake.estaAlvLaskut)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (value) {
          this.asiakas.estaAlvVerkkolaskujenLahetys = 1
        } else {
          delete this.asiakas.estaAlvVerkkolaskujenLahetys
        }
      })

    this._tilaaTekstikentta(this.lomake.viitenumero, value => {
      this.asiakas.viitenumero = this._validationService.processValue(value)
    })

    this._tilaaTekstikentta(this.lomake.hinnanPerusteet, value => {
      this.asiakas.hinnanPerusteet = this._validationService.processValue(value)
    })

    muutokset(this.lomake.spostiVaiEmail)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: Laskutustyyppi) => {
        if (value === 'verkkolasku') {
          this.naytaSahkoinenLaskutus.set(true)
          asetaArvo(this.lomake.sahkoinenLaskuValittaja, null)
          this._asetaTekstikentta(this.lomake.sahkoinenLaskuOsoite, null)
        } else {
          this.naytaSahkoinenLaskutus.set(false)
          asetaArvo(this.lomake.sahkoinenLaskuValittaja, null)
          this._asetaTekstikentta(this.lomake.sahkoinenLaskuOsoite, null)
        }
      })

    muutokset(this.lomake.sahkoinenLaskuValittaja)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (!this.asiakas.sahkoinenLaskutusosoite) {
          this.asiakas.sahkoinenLaskutusosoite = { sahkoinenOsoite: null, sahkoinenValittaja: null }
        }
        if (value && (value as Verkkolaskuoperaattori).tunnus) {
          this.asiakas.sahkoinenLaskutusosoite.sahkoinenValittaja = this._validationService.processValue((value as Verkkolaskuoperaattori).tunnus)
        } else {
          this.asiakas.sahkoinenLaskutusosoite.sahkoinenValittaja = this._validationService.processValue(value)
        }
      })
    this._tilaaTekstikentta(this.lomake.sahkoinenLaskuOsoite, value => {
      if (!this.asiakas.sahkoinenLaskutusosoite) {
        this.asiakas.sahkoinenLaskutusosoite = { sahkoinenOsoite: null, sahkoinenValittaja: null }
      }
      this.asiakas.sahkoinenLaskutusosoite.sahkoinenOsoite = this._validationService.processValue(value)
    })

    // this.laskunKieli.valueChanges.pipe(
    //   takeUntil(this.ngUnsubscribe)
    // ).subscribe(value => {
    //   if (value === 'fi') {
    //     this.asiakas.laskunKieli = 'fi'
    //   } else if (value === 'en') {
    //     this.asiakas.laskunKieli = 'en'
    //   }
    // })

    // Kentän muutos poisti reaktiivisessa lomakkeessa puuttuvan vastaanottajan virheen.
    muutokset(this.lomake.lisaavastaanottaja)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this._vastaanottajaPuuttuu.set(false)
      })

    this.verkkolaskuoperaattorit = muutokset(this.lomake.sahkoinenLaskuValittaja).pipe(
      startWith(''),
      map(value => this._filterVerkkolaskuoperaattori(value)),
    )

    combineLatest([this._asiakasService.nykyinenAsiakasObservable, this.naytaSuperObservable])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([asiakas, onSuper]) => {
        if (asiakas) {
          this.onSuper.set(onSuper)
          this.asiakas = this._asiakasKopioija.kopioiAsiakas(asiakas)
          this.alustaLomakkeenTiedot(this.asiakas)
          this._changeDetectorRef.markForCheck()
        }
      })

    this.apixForwardConfigObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (!asiakas) {
          return of<ApixReceivedInvoiceConfig>(null)
        }
        const uri = 'customers/' + asiakas.avain + '/apix-received-invoice-config/' + asiakas.avain
        return this._lemonaidFirebase.firestoreDoc<ApixReceivedInvoiceConfig>(uri).listen()
      }),
    )

    this.tyokieltoHistoriaObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(avainTiedot => {
        if (!avainTiedot?.avain) {
          return of<TyokieltoHistoriaEntry[]>([])
        }
        const uri = this._asiakasUriService.annaTyokieltoHistoriaCollectionUri(avainTiedot.avain)
        return this._firebase.firestoreCollection<TyokieltoHistoriaEntry>(uri).orderBy('paivitetty', 'asc').listen()
      }),
    )

    this.seuraavaLaskuObservable = combineLatest([this._asiakasService.nykyinenAsiakasObservable, this._esikatseluKuukausia]).pipe(
      switchMap(async ([asiakas, kuukausia]) => {
        if (!asiakas || !asiakas.muutJatkuvatVeloitukset || asiakas.muutJatkuvatVeloitukset.length === 0) {
          return null
        }

        const aktiivisetVeloitukset = this._veloituksetService.annaAktiivisetMuutVeloitukset(asiakas.muutJatkuvatVeloitukset)
        if (aktiivisetVeloitukset.length < 1) {
          return null
        }

        const kuukausi = this._dateService.kuukaudenEnsimmainen(this._dateService.lisaaKuukausia(new Date(), kuukausia))
        const seuraavaKuukausiLocalDate = this._dateService.dateToLocalDate(kuukausi)

        const kuukaudenNimi = this._dateService.annaKuukaudenNimiPaikallinen(seuraavaKuukausiLocalDate, 'fi', 'long')
        const kuukausiJaVuosi = kuukaudenNimi.toLowerCase() + ' ' + seuraavaKuukausiLocalDate.year

        // Tarkistetaan samat ehdot kuin lähetyksessä
        const sopimusOnVoimassaTassaKuussa = this._asiakasJaettuService.onkoSopimusVoimassaAinakinYhdenPaivanKuukaudessa(asiakas.sopimuskaudet, seuraavaKuukausiLocalDate)
        const kuukaudenLoppu = this._dateService.kuukaudenViimeinenPaikallinen(seuraavaKuukausiLocalDate)
        const tauko = this._sopimuskausiService.annaKokoAikavalinVoimassaOlevaTauko(asiakas, seuraavaKuukausiLocalDate, kuukaudenLoppu)

        // Tarkistetaan pitääkö lähettää veloituksia
        const pitaakoLahettaa = this._veloituksetService.pitaakoLahettaaMuutJatkuvatVeloitukset(sopimusOnVoimassaTassaKuussa, tauko, aktiivisetVeloitukset)

        if (!pitaakoLahettaa) {
          return {
            kuukausi: kuukausiJaVuosi,
            rivit: [],
            yhteensa: 0,
          }
        }

        const tulevatVeloitukset = this._veloituksetService.annaMuutJatkuvatVeloituksetKuukaudelle(aktiivisetVeloitukset, seuraavaKuukausiLocalDate)
        const veloituksetNaytettavaksi = tauko ? tulevatVeloitukset.filter(v => this._veloituksetService.onkoTauollaSallittuTuote(v.tuote, tauko)) : tulevatVeloitukset

        const rivit = await Promise.all(
          veloituksetNaytettavaksi.map(async veloitus => {
            return {
              nimi: await this._tuotteetService.annaTuoteNimiLisatiedoillaKielella(veloitus.tuote, 'fi', veloitus.lisatiedot),
              hinta: veloitus.hinta,
            }
          }),
        )

        const yhteensa = rivit.reduce((sum, rivi) => sum + rivi.hinta, 0)

        return {
          kuukausi: kuukausiJaVuosi,
          rivit,
          yhteensa,
        }
      }),
    )
    this._changeDetectorRef.markForCheck()
  }

  // get laskunKieli() {
  //   return this.form.get('laskunKieli')
  // }

  private _tilaaTekstikentta(kentta: FieldTree<string>, kasittelija: (arvo: string | null) => void) {
    this._tekstikenttienKasittelijat.set(kentta, kasittelija)
    arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this.ngUnsubscribe)).subscribe(kasittelija)
  }

  // Tekstikentän malliarvo ei voi olla null, mutta tilaaja saa asetetun arvon sellaisenaan kuten reaktiivisen lomakkeen setValue-kutsussa.
  private _asetaTekstikentta(kentta: FieldTree<string>, arvo: string | null) {
    asetaArvo(kentta, arvo ?? '', { ilmoita: false })
    this._tekstikenttienKasittelijat.get(kentta)?.(arvo)
  }

  private _rivinAsetukset(rivi: object): RivinAsetukset {
    return this._rivienAsetukset.get(rivi) ?? {}
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytetaankoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return this.onkoVirhe(kentta, laji) && (tila.dirty() || tila.touched())
  }

  // Rivin lisäys ja poisto eivät lähetä muiden rivien kenttien arvoja uudelleen, kuten FormArrayn push, insert, removeAt ja clear.
  private _lisaaRivi<T>(nimi: Rivitaulukko, taulukko: FieldTree<T[]>, rivi: T, asetukset: RivinAsetukset, indeksi?: number) {
    const rivit = [...taulukko().value()]
    const kohta = indeksi ?? rivit.length
    rivit.splice(kohta, 0, rivi)
    taulukko().value.set(rivit)
    const kentta = taulukko[kohta]
    this._rivienAsetukset.set(kentta, asetukset)
    const tilaukset = new Subscription()
    if (!this._rivienTilaukset.has(nimi)) {
      this._rivienTilaukset.set(nimi, [])
    }
    this._rivienTilaukset.get(nimi).splice(kohta, 0, tilaukset)
    const muutokset = <K>(alikentta: FieldTree<K>, kasittelija: (arvo: K) => void) => {
      tilaukset.add(arvonMuutokset(alikentta, { injector: this._injector }).pipe(takeUntil(this.ngUnsubscribe)).subscribe(kasittelija))
    }
    return { kentta, muutokset }
  }

  private _poistaRivi<T>(nimi: Rivitaulukko, taulukko: FieldTree<T[]>, index: number) {
    const tilaukset = this._rivienTilaukset.get(nimi) ?? []
    tilaukset[index]?.unsubscribe()
    tilaukset.splice(index, 1)
    taulukko().value.set(
      taulukko()
        .value()
        .filter((_, i) => i !== index),
    )
  }

  private _tyhjennaRivit<T>(nimi: Rivitaulukko, taulukko: FieldTree<T[]>) {
    for (const tilaukset of this._rivienTilaukset.get(nimi) ?? []) {
      tilaukset.unsubscribe()
    }
    this._rivienTilaukset.set(nimi, [])
    taulukko().value.set([])
  }

  private _rivinIndeksi<T>(taulukko: FieldTree<T[]>, rivi: object): number {
    return [...taulukko].findIndex(kentta => kentta === rivi)
  }

  private _filterVerkkolaskuoperaattori(value: string | Verkkolaskuoperaattori): Verkkolaskuoperaattori[] {
    if (value === '' || !value) {
      return []
    }
    let filterValue = ''
    if (value instanceof String || typeof value === 'string') {
      filterValue = value.toLowerCase()
    } else {
      filterValue = value && value.nimi ? value.nimi.toLowerCase() : ''
    }
    return this._sahkoisenLaskunValittajaService
      .annaKaikkiValittajat()
      .filter(operaattori => operaattori.nimi.toLowerCase().includes(filterValue) || operaattori.tunnus.toLowerCase().includes(filterValue))
  }

  private _annaSahkoisenLaskunOsoitteenValidointiFunktio(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        const ctrlValue = this._stringService.removeAllWhiteSpaces(control.value)
        const valittajat = this._sahkoisenLaskunValittajaService.annaKaikkiValittajat()
        for (const valittaja of valittajat) {
          if (this._stringService.removeAllWhiteSpaces(valittaja.tunnus) === ctrlValue) {
            return { valittaja: true }
          }
        }
      }
      return null
    }
  }

  valittajaDisplayFn(operaattori?: Verkkolaskuoperaattori): string | undefined {
    if (operaattori && operaattori.nimi) {
      return operaattori.nimi + ' (' + operaattori.tunnus + ')'
    }
    if (operaattori instanceof String || typeof operaattori === 'string') {
      return operaattori as any as string
    }
    return undefined
  }

  lisaaLaskunVastaanottaja() {
    const field = this.lomake.lisaavastaanottaja
    this._vastaanottajaPuuttuu.set(false)
    this.lisaaEmail(field)
  }

  private lisaaEmail(field: FieldTree<string>) {
    // console.log('raaka', field().value())
    if ((field().value() || '').trim()) {
      let arvo = field().value().trim()
      arvo = arvo.replace(/,/g, '').trim()
      // console.log('kasitelty', arvo)
      if (!Validators.email({ value: arvo } as AbstractControl)) {
        if (!this.asiakas.laskunVastaanottajat) {
          this.asiakas.laskunVastaanottajat = []
        }
        this.asiakas.laskunVastaanottajat.push(arvo)
        asetaArvo(field, '')
      }
    }
  }

  poistaLaskunVastaanottaja(vastaanottaja: string): void {
    const index = this.asiakas.laskunVastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.asiakas.laskunVastaanottajat.splice(index, 1)
    }
  }

  private alustaLomakkeenTiedot(asiakas: Asiakas) {
    this._tyhjennaRivit('hinnat', this.lomake.hinnat)
    this._lisatytNollahinnat.clear()

    this._tyhjennaRivit('muutJatkuvatVeloitukset', this.lomake.muutJatkuvatVeloitukset)

    const valittaja = asiakas?.sahkoinenLaskutusosoite?.sahkoinenValittaja || null
    const osoite = asiakas?.sahkoinenLaskutusosoite?.sahkoinenOsoite || null
    const valittajaObject = this._sahkoisenLaskunValittajaService.annaValittaja(valittaja)

    // This must be set first as it nullifies the valittaja && osoite
    const tyyppi: Laskutustyyppi = valittaja ? 'verkkolasku' : 'spostilasku'
    asetaArvo(this.lomake.spostiVaiEmail, tyyppi)

    asetaArvo(this.lomake.sahkoinenLaskuValittaja, valittajaObject || valittaja || null)
    this._asetaTekstikentta(this.lomake.sahkoinenLaskuOsoite, osoite || null)

    this.asiakkaanOsoitetiedotPuuttuvat.set(!asiakas?.katuosoite || !asiakas?.postitmp || !asiakas?.postinro)

    if (asiakas) {
      this._asetaTekstikentta(this.lomake.hinnanPerusteet, asiakas.hinnanPerusteet)
      // this.form.get('laskunKieli').setValue(asiakas.laskunKieli)
      this._asetaTekstikentta(this.lomake.viitenumero, this._viitenumeroService.muotoileViitenumero(asiakas.viitenumero))
      if (asiakas.hinnat) {
        for (const hinta of asiakas.hinnat) {
          this._lisaaHintarivi(hinta, false, true)
        }
      }
      if (asiakas.muutJatkuvatVeloitukset) {
        this.onPoistettujaVeloituksia.set(asiakas.muutJatkuvatVeloitukset.some(v => v.poistettu))
        for (const veloitus of asiakas.muutJatkuvatVeloitukset) {
          this._lisaaJatkuvanVeloituksenRivi(veloitus)
        }
      } else {
        this.onPoistettujaVeloituksia.set(false)
      }
      asetaArvo(this.lomake.estaAlvLaskut, !!asiakas.estaAlvVerkkolaskujenLahetys)
    } else {
      this._asetaTekstikentta(this.lomake.viitenumero, '')
      this._asetaTekstikentta(this.lomake.hinnanPerusteet, '')
      asetaArvo(this.lomake.estaAlvLaskut, false)
    }
  }

  private edellinenKuukausiValidator = (arvo: Date, index: number): ValidationErrors | null => {
    if (arvo) {
      const rivit = this.lomake.hinnat().value()
      if (index > 0) {
        const edellinen = rivit[index - 1]
        if (edellinen) {
          if (edellinen.voimassaAlkaen) {
            // console.log('Validoidaan nykyinen: ', arvo.toISOString())
            // console.log('Validoidaan edellinen:', edellinen.voimassaAlkaen.toISOString())
            // console.log('Kuukausia välissä:', this.dateService.kuukausiaValissa(arvo, edellinen.voimassaAlkaen))
            if (this._dateService.kuukausiaValissa(arvo, edellinen.voimassaAlkaen) < 1) {
              return { edellinen: true }
            }
          }
        }
      }
    }
    return null
  }

  private luoPaattymispaivamaaraFilter(rivi: FieldTree<MuuJatkuvaVeloitusRivi>): (date: Date | null) => boolean {
    return (date: Date | null): boolean => {
      if (!date) {
        return true
      }
      // Frekvenssi luetaan seuraamatta, koska sen muutos ei tarkistanut päättymispäivää uudelleen.
      return !this.tarkistaPaattymispaiva(
        date,
        rivi.voimassaAlkaen().value(),
        untracked(() => rivi.frekvenssi().value()),
      )
    }
  }

  annaPaattyyFilter(rivi: FieldTree<MuuJatkuvaVeloitusRivi>) {
    return this._rivinAsetukset(rivi).paattyyFilter
  }

  private paattymisPaivamaaraValidator = (paattyy: Date, alkaa: Date, frekvenssi: LaskutusFrekvenssi) => {
    if (!paattyy) {
      return null
    }

    return this.tarkistaPaattymispaiva(paattyy, alkaa, frekvenssi)
  }

  private tarkistaPaattymispaiva(paattyy: Date, alkaa: Date, frekvenssi: LaskutusFrekvenssi): { ennenAlkua?: boolean; virheellinenPaattymisPaiva?: boolean } | null {
    if (!paattyy || !alkaa) {
      return null
    }
    const kuukausiaValissa = this._dateService.kuukausiaValissa(paattyy, alkaa)
    if (kuukausiaValissa < 0) {
      return { ennenAlkua: true }
    }

    if (!frekvenssi || frekvenssi === '1kk') {
      return null
    }

    const frekvenssiKuukausina = frekvenssi === '3kk' ? 3 : 12
    if (kuukausiaValissa % frekvenssiKuukausina !== 0) {
      return { virheellinenPaattymisPaiva: true }
    }
    return null
  }

  private voimassaAlkaenDayValidator = (arvo: Date): ValidationErrors | null => {
    if (arvo) {
      const localDate = this._dateService.dateToLocalDate(arvo)
      if (localDate.day !== 1) {
        return { eiKuukaudenAlku: true }
      }
    }
    return null
  }

  private _lisaaHintarivi(hinta: AsiakkaanKuukausihinta, focus: boolean, lukitseVanhat: boolean, indeksi?: number) {
    // const d = new Date()
    // d.setDate(2)
    // console.log('PÄIVÄ', d.toISOString(), this.dateService.lisaaKuukausia(d, 1).toISOString())

    const paiva = this._dateService.localDateToDate(hinta.voimassaAlkaen)
    // const lukossa = false
    const lukossa = this._dateService.kuukausiaValissa(paiva, new Date()) < 1 && lukitseVanhat
    const muutoksenSyy = lukossa ? hinta.muutoksenSyy || ' ' : hinta.muutoksenSyy || ''
    const { kentta, muutokset } = this._lisaaRivi('hinnat', this.lomake.hinnat, { voimassaAlkaen: paiva, hinta: hinta.hinta ?? null, syy: muutoksenSyy || '' }, { lukossa }, indeksi)

    muutokset(kentta.voimassaAlkaen, value => {
      hinta.voimassaAlkaen = this._dateService.dateToLocalDate(value)
    })
    muutokset(kentta.hinta, value => {
      hinta.hinta = value
    })
    muutokset(kentta.syy, value => {
      hinta.muutoksenSyy = value
    })

    if (focus) {
      setTimeout(() => {
        this.hintaKomponentit.last.focus()
        this._changeDetectorRef.markForCheck()
      }, 10)
      this._changeDetectorRef.markForCheck()
    }

    return kentta
  }

  annaMinimiPaivamaara<T>(kentta: FieldTree<T>): Date {
    if (!kentta().disabled()) {
      const d = new Date()
      d.setDate(1)
      d.setHours(0)
      d.setMinutes(0)
      d.setSeconds(0)
      d.setMilliseconds(1)
      return this._dateService.lisaaKuukausia(d, 1)
    }
    return new Date(1980, 1, 1)
  }

  private _lisaaJatkuvanVeloituksenRivi(veloitus: AsiakkaanMuuJatkuvaVeloitus) {
    const alkamispaiva = veloitus.voimassaAlkaen ? this._dateService.localDateToDate(veloitus.voimassaAlkaen) : null
    const lukossa = alkamispaiva && this._dateService.kuukausiaValissa(alkamispaiva, new Date()) <= 0

    const { kentta, muutokset } = this._lisaaRivi(
      'muutJatkuvatVeloitukset',
      this.lomake.muutJatkuvatVeloitukset,
      {
        tuote: veloitus.tuote ?? null,
        hinta: veloitus.hinta ?? null,
        frekvenssi: veloitus.frekvenssi ?? null,
        voimassaAlkaen: alkamispaiva,
        paattyy: veloitus.paattyy ? this._dateService.localDateToDate(veloitus.paattyy) : null,
        lisatiedot: veloitus.lisatiedot || '',
      },
      { lukossa: !!lukossa },
    )

    muutokset(kentta.tuote, async value => {
      veloitus.tuote = value
      veloitus.paivitetty = this._timestampService.now()

      // Ehdotetaan hintaa vain jos ei jo arvoa
      if ((kentta.hinta().value() === null || kentta.hinta().value() === undefined) && value) {
        const tuote = await this._tuotteetService.annaTuotenimikeAvaimella(value)
        if (tuote?.hinta) {
          asetaArvo(kentta.hinta, tuote.hinta)
        }
      }

      // Reaktiivisen lomakkeen updateValueAndValidity lähetti hinnan uudelleen tilaajille.
      asetaArvo(kentta.hinta, kentta.hinta().value())
    })
    muutokset(kentta.hinta, value => {
      veloitus.hinta = value
      veloitus.paivitetty = this._timestampService.now()
    })
    muutokset(kentta.frekvenssi, value => {
      veloitus.frekvenssi = value
      veloitus.paivitetty = this._timestampService.now()
    })
    muutokset(kentta.voimassaAlkaen, value => {
      veloitus.voimassaAlkaen = value ? this._dateService.dateToLocalDate(value) : null
      veloitus.paivitetty = this._timestampService.now()
    })
    muutokset(kentta.paattyy, value => {
      veloitus.paattyy = value ? this._dateService.dateToLocalDate(this._dateService.kuukaudenViimeinen(value)) : null
      veloitus.paivitetty = this._timestampService.now()
    })
    muutokset(kentta.lisatiedot, value => {
      veloitus.lisatiedot = value
      veloitus.paivitetty = this._timestampService.now()
    })

    if (veloitus.voimassaAlkaen && kentta.voimassaAlkaen().invalid()) {
      kentta.voimassaAlkaen().markAsTouched()
    }

    this._rivinAsetukset(kentta).paattyyFilter = this.luoPaattymispaivamaaraFilter(kentta)
  }

  private annaUusiHinta(): AsiakkaanKuukausihinta {
    const d = new Date()
    d.setDate(2)
    return {
      hinta: null,
      voimassaAlkaen: this._dateService.dateToLocalDate(this._dateService.lisaaKuukausia(d, 1)),
      muutoksenSyy: null,
      muutosPvm: this._timestampService.now(),
    }
  }

  poistaHintarivi(rivi: FieldTree<HintaRivi>) {
    const index = this._rivinIndeksi(this.lomake.hinnat, rivi)
    if (rivi.hinta().disabled() || index < 1) {
      return
    }
    this._poistaRivi('hinnat', this.lomake.hinnat, index)
    this.asiakas.hinnat.splice(index, 1)
    this._lisatytNollahinnat.delete(rivi)
  }

  onkoLisattyNollahinta(rivi: FieldTree<HintaRivi>) {
    return this._lisatytNollahinnat.has(rivi)
  }

  valitseNollahinnanKuukausi(kuukausi: Date, datepicker: MatDatepicker<Date>) {
    const kentta = this.nollahintaLomake.kuukausi
    asetaArvo(kentta, kuukausi)
    kentta().markAsTouched()
    datepicker.close()
  }

  lisaaNollahintaMenneelleKuukaudelle() {
    // Reaktiivinen lomake tarkisti kuukauden uudelleen asiakkaan nykyisiä hintoja vasten.
    this._nollahinnanTarkistus.update(kerta => kerta + 1)
    if (!this.nollahintaLomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.nollahintaLomake)
      return
    }

    const voimassaAlkaen = this._dateService.dateToLocalDate(this._dateService.kuukaudenEnsimmainen(this.nollahintaLomake.kuukausi().value()))
    const hinta: AsiakkaanKuukausihinta = {
      hinta: 0,
      voimassaAlkaen,
      muutoksenSyy: this.nollahintaLomake.syy().value().trim(),
      muutosPvm: this._timestampService.now(),
    }

    if (!this.asiakas.hinnat) {
      this.asiakas.hinnat = []
    }
    const seuraavanIndeksi = this.asiakas.hinnat.findIndex(h => this._dateService.compareLocalMonths(h.voimassaAlkaen, '>', voimassaAlkaen))
    const indeksi = seuraavanIndeksi < 0 ? this.asiakas.hinnat.length : seuraavanIndeksi

    this.asiakas.hinnat.splice(indeksi, 0, hinta)
    const rivi = this._lisaaHintarivi(hinta, false, true, indeksi)
    this.lomake.hinnat().markAsDirty()
    this._lisatytNollahinnat.add(rivi)

    nollaa(this.nollahintaLomake, TYHJA_NOLLAHINTA)
  }

  lisaaHintarivi() {
    const hinta = this.annaUusiHinta()
    this._lisaaHintarivi(hinta, true, false)

    if (!this.asiakas.hinnat) {
      this.asiakas.hinnat = []
    }
    this.asiakas.hinnat.push(hinta)
  }

  async lisaaMuujatkuvaVeloitusrivi() {
    const kirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
    const veloitus: AsiakkaanMuuJatkuvaVeloitus = {
      avain: this._firebase.firestoreCreateId(),
      tuote: null,
      hinta: null,
      frekvenssi: null,
      voimassaAlkaen: null,
      paattyy: null,
      lisatiedot: null,
      poistettu: false,
      luoja: kirjanpitaja?.uid || null,
      luotu: this._timestampService.now(),
      paivittaja: kirjanpitaja?.uid || null,
      paivitetty: this._timestampService.now(),
    }

    this._lisaaJatkuvanVeloituksenRivi(veloitus)

    if (!this.asiakas.muutJatkuvatVeloitukset) {
      this.asiakas.muutJatkuvatVeloitukset = []
    }
    this.asiakas.muutJatkuvatVeloitukset.push(veloitus)
  }

  esikatseluEdellinenKuukausi() {
    const nykyinen = this._esikatseluKuukausia.value
    this._esikatseluKuukausia.next(nykyinen - 1)
  }

  esikatseluSeuraavaKuukausi() {
    const nykyinen = this._esikatseluKuukausia.value
    this._esikatseluKuukausia.next(nykyinen + 1)
  }

  toggleNaytaPoistetut() {
    this.naytaPoistetut = !this.naytaPoistetut
  }

  onkoVeloitusPoistettu(index: number): boolean {
    return this.asiakas?.muutJatkuvatVeloitukset?.[index]?.poistettu ? true : false
  }

  annaVeloituksenPoistotiedot(index: number): AsiakkaanMuuJatkuvaVeloitus | null {
    if (!this.asiakas?.muutJatkuvatVeloitukset?.[index]?.poistettu) {
      return null
    }
    return this.asiakas.muutJatkuvatVeloitukset[index]
  }

  poistaMuuJatkuvaVeloitusrivi(rivi: FieldTree<MuuJatkuvaVeloitusRivi>) {
    const index = this._rivinIndeksi(this.lomake.muutJatkuvatVeloitukset, rivi)
    if (index > -1 && this.asiakas.muutJatkuvatVeloitukset) {
      const veloitus = this.asiakas.muutJatkuvatVeloitukset[index]

      if (!veloitus.tuote || !veloitus.voimassaAlkaen) {
        this._poistaRivi('muutJatkuvatVeloitukset', this.lomake.muutJatkuvatVeloitukset, index)
        this.asiakas.muutJatkuvatVeloitukset.splice(index, 1)
      } else {
        const veloitusKopio: AsiakkaanMuuJatkuvaVeloitus = {
          ...veloitus,
          voimassaAlkaen: veloitus.voimassaAlkaen ? { ...veloitus.voimassaAlkaen } : null,
          paattyy: veloitus.paattyy ? { ...veloitus.paattyy } : null,
        }
        this._poistetutVeloitukset.push(veloitusKopio)
        this._poistaRivi('muutJatkuvatVeloitukset', this.lomake.muutJatkuvatVeloitukset, index)
        this.asiakas.muutJatkuvatVeloitukset.splice(index, 1)
      }
    }
  }

  chosenMonthHandler(normalizedMonth: Date, rivi: FieldTree<HintaRivi>, datepicker: MatDatepicker<Date>) {
    const kentta = rivi.voimassaAlkaen
    const minDate = this.annaMinimiPaivamaara(kentta)
    if (this._dateService.kuukausiaValissa(normalizedMonth, minDate) > -1) {
      asetaArvo(kentta, normalizedMonth)
      kentta().markAsTouched()
    }
    datepicker.close()
  }

  chosenMonthHandlerVeloitus(normalizedMonth: Date, rivi: FieldTree<MuuJatkuvaVeloitusRivi>, datepicker: MatDatepicker<Date>, fieldName: 'voimassaAlkaen' | 'paattyy' = 'voimassaAlkaen') {
    const kentta = rivi[fieldName]
    const minDate = this.annaMinimiPaivamaara(kentta)
    if (this._dateService.kuukausiaValissa(normalizedMonth, minDate) > -1) {
      asetaArvo(kentta, normalizedMonth)
      kentta().markAsTouched()
    }
    datepicker.close()
  }

  async save() {
    if (!this.naytaSahkoinenLaskutus() && (!this.asiakas || !this.asiakas.laskunVastaanottajat || this.asiakas.laskunVastaanottajat.length < 1)) {
      this._vastaanottajaPuuttuu.set(true)
    }

    if (this.naytaSahkoinenLaskutus() && this.asiakkaanOsoitetiedotPuuttuvat()) {
      return
    }

    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    // Käsitellään poistetut veloitukset
    if (this._poistetutVeloitukset.length > 0) {
      const kirjanpitaja = await firstValueFrom(this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable)
      for (const veloitus of this._poistetutVeloitukset) {
        const onkoListassa = this.asiakas.muutJatkuvatVeloitukset.some(v => v.avain === veloitus.avain && v.poistettu)
        if (!onkoListassa) {
          veloitus.poistettu = true
          veloitus.paivitetty = this._timestampService.now()
          if (kirjanpitaja) {
            veloitus.paivittaja = kirjanpitaja.uid || null
          }
          this.asiakas.muutJatkuvatVeloitukset.push(veloitus)
        }
      }
      this._poistetutVeloitukset = []
    }
    this._ladataanService.aloitaLataaminen()
    // console.log(this.asiakas)
    this._asiakasService
      .paivitaAsiakas(this.asiakas, 'tilikaudet-eivat-voineet-muuttua', true, false)
      .then(() => {
        this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
        this._snackbar.open('Laskutustiedot tallennettiin onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'top' })
        this._ladataanService.lopetaLataaminen()
      })
      .catch(err => {
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
        this._snackbar.open('TALLENNUS EPÄONNISTUI. Kerro terveiset ylläpidolle.', 'Pilalla', { duration: 10000, verticalPosition: 'top' })
      })
  }

  lisaa() {
    this._router.navigate(['asiakkaat', this.asiakas.avain, 'laskutus', 'luo', UUDEN_LASKUN_AVAIN, UUDEN_LASKUN_AVAIN]).then(result => {
      this._vasenValikkoService.paivitaAuki(false)
    })
  }

  annaTyokieltoTapahtumaKuvaus(entry: TyokieltoHistoriaEntry): string {
    return this._optionLabelsService.tyokieltoTapahtumaTyyppi(entry.tyyppi)
  }

  annaTyokieltoPaivittajanNimi(entry: TyokieltoHistoriaEntry): string {
    if (entry.paivittaja === 'lemonator_system') {
      return 'Lemonatorin taustajärjestelmä'
    }
    const paivittaja = this.kirjanpitajatMap().get(entry.paivittaja)
    return paivittaja ? `${paivittaja.etunimi} ${paivittaja.sukunimi}` : 'Tuntematon'
  }
}
